import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ExcelJS from "exceljs";
import { getUserData } from "../../../services/apiService";
import { resolvePlatformSuperAdminStaffId } from "../../../utils/permissionUtils";
import {
  fetchFaqAiUsage,
  fetchOrgFaqAiSettings,
  saveOrgFaqAiFlag,
} from "../../../services/superAdminOrgService";
import "./SuperAdminFaqAiModal.scss";

function formatUtc(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().replace("T", " ").slice(0, 19);
}

function clampCap(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function CapStepper({
  id,
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onChange,
}) {
  return (
    <div className="faq-ai-modal__cap">
      <label className="faq-ai-modal__cap-label" htmlFor={id}>
        {label}
      </label>
      <div className="faq-ai-modal__stepper">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={disabled || value <= min}
          onClick={() => onChange(clampCap(value - 1, min, max))}
        >
          −
        </button>
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(clampCap(e.target.value, min, max))}
        />
        <button
          type="button"
          aria-label={`Increase ${label}`}
          disabled={disabled || value >= max}
          onClick={() => onChange(clampCap(value + 1, min, max))}
        >
          +
        </button>
      </div>
      <p className="faq-ai-modal__cap-hint">{hint}</p>
    </div>
  );
}

function SuperAdminFaqAiModal({
  org,
  open,
  onClose,
  onSaved,
  showAlert,
}) {
  const [enabled, setEnabled] = useState(false);
  const [formBuilderAiEnabled, setFormBuilderAiEnabled] = useState(false);
  const [allowClinicalPacks, setAllowClinicalPacks] = useState(true);
  const [allowGeneralForms, setAllowGeneralForms] = useState(false);
  const [orgCap, setOrgCap] = useState(50);
  const [userCap, setUserCap] = useState(50);
  const [saved, setSaved] = useState({
    enabled: false,
    formBuilderAiEnabled: false,
    allowClinicalPacks: true,
    allowGeneralForms: false,
    orgCap: 50,
    userCap: 50,
  });
  const [usage, setUsage] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [summary, setSummary] = useState(null);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const min = policy?.dailyCapMin || 1;
  const max = policy?.dailyCapMax || 500;
  const defaultOrg = policy?.dailyOrgCap || 50;
  const defaultUser = policy?.dailyUserCap || 50;

  const load = useCallback(async (organizationId) => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const createdBy = resolvePlatformSuperAdminStaffId(getUserData());
      const [settings, usageResult] = await Promise.all([
        fetchOrgFaqAiSettings({ organizationId, createdBy }),
        fetchFaqAiUsage({ organizationId, createdBy }).catch(() => ({
          rows: [],
          policy: null,
          summary: null,
          today: null,
        })),
      ]);
      const nextPolicy = usageResult.policy || null;
      const nextMin = nextPolicy?.dailyCapMin || 1;
      const nextMax = nextPolicy?.dailyCapMax || 500;
      const nextDefaultOrg = nextPolicy?.dailyOrgCap || 50;
      const nextDefaultUser = nextPolicy?.dailyUserCap || 50;
      const nextOrgCap = clampCap(
        settings.dailyOrgCap ?? nextDefaultOrg,
        nextMin,
        nextMax,
      );
      const nextUserCap = clampCap(
        settings.dailyUserCap ?? nextDefaultUser,
        nextMin,
        nextMax,
      );
      setPolicy(nextPolicy);
      setUsage(usageResult.rows || []);
      setSummary(usageResult.summary || null);
      setToday(usageResult.today || null);
      setEnabled(!!settings.enabled);
      const fbEnabled = !!settings.formBuilderAiEnabled;
      const modes = Array.isArray(settings.formBuilderAiModes)
        ? settings.formBuilderAiModes
        : [];
      const nextClinical = fbEnabled
        ? modes.includes("clinicalPacks") || modes.length === 0
        : true;
      const nextGeneral = fbEnabled ? modes.includes("generalForms") : false;
      setFormBuilderAiEnabled(fbEnabled);
      setAllowClinicalPacks(nextClinical);
      setAllowGeneralForms(nextGeneral);
      setOrgCap(nextOrgCap);
      setUserCap(nextUserCap);
      setSaved({
        enabled: !!settings.enabled,
        formBuilderAiEnabled: fbEnabled,
        allowClinicalPacks: nextClinical,
        allowGeneralForms: nextGeneral,
        orgCap: nextOrgCap,
        userCap: nextUserCap,
      });
    } catch (error) {
      showAlert?.({
        title: "Error",
        message: error?.message || "Could not load AQ Assist settings.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    if (!open || !org?.organizationId) return undefined;
    load(org.organizationId);
    return undefined;
  }, [open, org?.organizationId, load]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const dirty = useMemo(
    () =>
      enabled !== saved.enabled ||
      formBuilderAiEnabled !== saved.formBuilderAiEnabled ||
      allowClinicalPacks !== saved.allowClinicalPacks ||
      allowGeneralForms !== saved.allowGeneralForms ||
      orgCap !== saved.orgCap ||
      userCap !== saved.userCap,
    [
      enabled,
      formBuilderAiEnabled,
      allowClinicalPacks,
      allowGeneralForms,
      orgCap,
      userCap,
      saved,
    ],
  );

  const handleSave = useCallback(async () => {
    if (!org?.organizationId || !dirty) return;
    if (formBuilderAiEnabled && !allowClinicalPacks && !allowGeneralForms) {
      showAlert?.({
        title: "Form Builder AQ Assist",
        message:
          "Select at least one allowed category: Clinical packs and/or General forms.",
        type: "warning",
      });
      return;
    }
    setSaving(true);
    try {
      const createdBy = resolvePlatformSuperAdminStaffId(getUserData());
      const modes = [];
      if (allowClinicalPacks) modes.push("clinicalPacks");
      if (allowGeneralForms) modes.push("generalForms");
      const result = await saveOrgFaqAiFlag({
        organizationId: org.organizationId,
        enabled,
        dailyOrgCap: clampCap(orgCap, min, max),
        dailyUserCap: clampCap(userCap, min, max),
        formBuilderAiEnabled,
        formBuilderAiModes: modes,
        createdBy,
      });
      const nextOrgCap = clampCap(result.dailyOrgCap ?? orgCap, min, max);
      const nextUserCap = clampCap(result.dailyUserCap ?? userCap, min, max);
      const nextFb = !!result.formBuilderAiEnabled;
      const nextModes = Array.isArray(result.formBuilderAiModes)
        ? result.formBuilderAiModes
        : [];
      const nextClinical = nextFb
        ? nextModes.includes("clinicalPacks") || nextModes.length === 0
        : true;
      const nextGeneral = nextFb ? nextModes.includes("generalForms") : false;
      setEnabled(!!result.enabled);
      setFormBuilderAiEnabled(nextFb);
      setAllowClinicalPacks(nextClinical);
      setAllowGeneralForms(nextGeneral);
      setOrgCap(nextOrgCap);
      setUserCap(nextUserCap);
      setSaved({
        enabled: !!result.enabled,
        formBuilderAiEnabled: nextFb,
        allowClinicalPacks: nextClinical,
        allowGeneralForms: nextGeneral,
        orgCap: nextOrgCap,
        userCap: nextUserCap,
      });
      onSaved?.({
        organizationId: org.organizationId,
        enabled: !!result.enabled,
        formBuilderAiEnabled: nextFb,
        formBuilderAiModes: nextModes,
        dailyOrgCap: nextOrgCap,
        dailyUserCap: nextUserCap,
        message: result.message,
      });
    } catch (error) {
      showAlert?.({
        title: "Error",
        message: error?.message || "Could not save AQ Assist settings.",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  }, [
    org,
    dirty,
    enabled,
    formBuilderAiEnabled,
    allowClinicalPacks,
    allowGeneralForms,
    orgCap,
    userCap,
    min,
    max,
    onSaved,
    showAlert,
  ]);

  const exportUsage = useCallback(async () => {
    if (!usage.length) return;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("AQ Assist usage");
    sheet.addRow([
      "WhenUtc",
      "User",
      "Module",
      "Question",
      "Tokens",
      "CostGbp",
      "Success",
      "Error",
    ]);
    usage.forEach((row) => {
      sheet.addRow([
        row.createdUtc,
        row.staffEmail || row.staffName,
        row.module,
        row.question,
        row.totalTokens,
        row.costGbp,
        row.success ? 1 : 0,
        row.errorMessage || "",
      ]);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `faq-ai-usage-${org?.orgName || "org"}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }, [usage, org?.orgName]);

  if (!open || !org) return null;

  const modal = (
    <div className="faq-ai-modal-root" role="presentation">
      <div className="faq-ai-modal-root__backdrop" aria-hidden="true" />
      <div
        className="modal fade show d-block faq-ai-modal"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="super-admin-faq-ai-title"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title" id="super-admin-faq-ai-title">
                  Manage AQ Assist
                </h5>
                <p className="faq-ai-modal__org">{org.orgName || "—"}</p>
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
                disabled={saving}
              />
            </div>

            <div className="modal-body">
              {loading ? (
                <p className="text-muted mb-0">Loading AQ Assist settings…</p>
              ) : (
                <>
                  <section className="faq-ai-modal__block">
                    <div className="faq-ai-modal__flag">
                      <div>
                        <strong>AQ Assist (Help → FAQ)</strong>
                        <p>
                          When On, users in this organisation can use AQ Assist
                          on Help &gt; FAQ. Usage, tokens, and cost are logged
                          for Super Admin review. Answers do not use live study
                          or volunteer records.
                        </p>
                      </div>
                      <div className="form-check form-switch faq-ai-modal__switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="faq-ai-modal-enabled"
                          checked={enabled}
                          disabled={saving}
                          onChange={(e) => setEnabled(e.target.checked)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="faq-ai-modal-enabled"
                        >
                          {enabled ? "On" : "Off"}
                        </label>
                      </div>
                    </div>
                  </section>

                  <section className="faq-ai-modal__block">
                    <div className="faq-ai-modal__flag">
                      <div>
                        <strong>AQ Assist for Form Builder</strong>
                        <p>
                          Separate from FAQ. When On, users can open AQ Assist
                          in e-Source Form Builder. The tab is always visible;
                          if this is Off they see a warning when they click it
                          (same pattern as FAQ).
                        </p>
                      </div>
                      <div className="form-check form-switch faq-ai-modal__switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="faq-ai-modal-form-builder"
                          checked={formBuilderAiEnabled}
                          disabled={saving}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setFormBuilderAiEnabled(on);
                            if (on && !allowClinicalPacks && !allowGeneralForms) {
                              setAllowClinicalPacks(true);
                            }
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="faq-ai-modal-form-builder"
                        >
                          {formBuilderAiEnabled ? "On" : "Off"}
                        </label>
                      </div>
                    </div>

                    <h3 className="faq-ai-modal__subhead">
                      Form Builder — allowed categories
                    </h3>
                    <p className="faq-ai-modal__lead">
                      Choose what AQ Assist may generate. Out-of-scope prompts
                      show a warning and do not change the form.
                    </p>
                    <div className="faq-ai-modal__mode-list">
                      <label className="faq-ai-modal__mode-item">
                        <input
                          type="checkbox"
                          checked={allowClinicalPacks}
                          disabled={saving || !formBuilderAiEnabled}
                          onChange={(e) =>
                            setAllowClinicalPacks(e.target.checked)
                          }
                        />
                        <span>
                          <strong>Clinical packs</strong>
                          <em>
                            Vitals, IMP receipt/dispense, adverse event, lab /
                            sample, and similar clinical starters.
                          </em>
                        </span>
                      </label>
                      <label className="faq-ai-modal__mode-item">
                        <input
                          type="checkbox"
                          checked={allowGeneralForms}
                          disabled={saving || !formBuilderAiEnabled}
                          onChange={(e) =>
                            setAllowGeneralForms(e.target.checked)
                          }
                        />
                        <span>
                          <strong>General / everyday forms</strong>
                          <em>
                            Non-clinical intents (orders, feedback, attendance)
                            and generic free-text forms. Leave Off for clinical
                            organisations.
                          </em>
                        </span>
                      </label>
                    </div>
                  </section>

                  <section className="faq-ai-modal__block">
                    <h3>Daily ask limits</h3>
                    <p className="faq-ai-modal__lead">
                      Increase or decrease the daily caps for this organisation.
                      Azure is called only when the user clicks Send. Long
                      pastes and clinical IDs are still rejected and logged at
                      £0.
                    </p>
                    <div className="faq-ai-modal__caps">
                      <CapStepper
                        id="faq-ai-org-cap"
                        label="Organisation asks per UTC day"
                        hint={`Default ${defaultOrg}. Allowed range ${min}–${max}.`}
                        value={orgCap}
                        min={min}
                        max={max}
                        disabled={saving}
                        onChange={setOrgCap}
                      />
                      <CapStepper
                        id="faq-ai-user-cap"
                        label="Asks per user per UTC day"
                        hint={`Default ${defaultUser}. One user cannot use the whole organisation cap.`}
                        value={userCap}
                        min={min}
                        max={max}
                        disabled={saving}
                        onChange={setUserCap}
                      />
                    </div>
                  </section>

                  <section className="faq-ai-modal__block">
                    <h3>Safety and operations</h3>
                    <ul className="faq-ai-modal__rules">
                      <li>
                        The assistant generates the how-to answer so customers
                        can stay in Help &gt; FAQ. It uses the AQ Trials
                        screen map and published FAQs. Source code is not sent
                        to Azure. If it still cannot answer safely, it points
                        to Contact support.
                      </li>
                      <li>
                        No study, volunteer, or CAPA records. No Bing, web, or
                        playground files. English only.
                      </li>
                      <li>
                        Failed asks are logged with the error and 0 tokens.
                        Logs stay {policy?.retentionMonths || 18} months, then
                        are purged. This environment is{" "}
                        {policy?.environmentName || "—"}. Set an Azure Portal
                        budget alert of £
                        {policy?.azureBudgetAlertGbpMin || 20}–£
                        {policy?.azureBudgetAlertGbpMax || 50}.
                      </li>
                    </ul>
                  </section>

                  <section className="faq-ai-modal__block">
                    <div className="faq-ai-modal__kpis">
                      <div>
                        <span>Today (org)</span>
                        <strong>
                          {today
                            ? `${today.orgUsed} / ${orgCap}`
                            : "—"}
                        </strong>
                      </div>
                      <div>
                        <span>Logged requests</span>
                        <strong>{summary?.requestCount ?? 0}</strong>
                      </div>
                      <div>
                        <span>Failed / rejected</span>
                        <strong>{summary?.failedCount ?? 0}</strong>
                      </div>
                      <div>
                        <span>Cost in log (£)</span>
                        <strong>
                          {Number(summary?.costGbp || 0).toFixed(6)}
                        </strong>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Usage</strong>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={loading || usage.length === 0}
                        onClick={exportUsage}
                      >
                        Export Excel
                      </button>
                    </div>
                    {usage.length === 0 ? (
                      <p className="text-muted mb-0">No requests logged yet.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>When (UTC)</th>
                              <th>User</th>
                              <th>Module</th>
                              <th>Tokens</th>
                              <th>Cost (£)</th>
                              <th>Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usage.slice(0, 50).map((row) => (
                              <tr
                                key={
                                  row.requestId ||
                                  `${row.createdUtc}-${row.question}`
                                }
                              >
                                <td>{formatUtc(row.createdUtc)}</td>
                                <td>
                                  {row.staffEmail || row.staffName || "—"}
                                </td>
                                <td>{row.module || "—"}</td>
                                <td>{row.totalTokens}</td>
                                <td>
                                  {Number(row.costGbp || 0).toFixed(6)}
                                </td>
                                <td>
                                  {row.success
                                    ? "OK"
                                    : `Failed${
                                        row.errorMessage
                                          ? ` — ${row.errorMessage}`
                                          : ""
                                      }`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!dirty || saving || loading}
              >
                {saving ? "Saving…" : "Save AQ Assist"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default SuperAdminFaqAiModal;
